import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Avatar,
  Box,
  Typography,
  Grid,
  Link as MuiLink,
} from '@material-ui/core';
import { Block, CheckCircleOutline, DeleteOutline, Security, Timer } from '@material-ui/icons';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { IImpersonationDashboard, IImpersonationRequest, PropertyError } from '../../../../Api/AptorApi';
import { useAptorApi } from '../../../../Api';
import { useApiValidationSnackbar } from '../../../../Api/useValidationSnackbar';
import { useStyles } from './ImpersonationRequestsWidget.styles';

const emptyDashboard: IImpersonationDashboard = { receivedRequests: [] };
const reasonPreviewLength = 100;

export const ImpersonationRequestsWidget = () => {
  const classes = useStyles();
  const { api, abortController } = useAptorApi();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { notifyUnhandledException } = useApiValidationSnackbar();
  const [dashboard, setDashboard] = useState<IImpersonationDashboard>(emptyDashboard);
  const [respondingId, setRespondingId] = useState<string>();
  const [now, setNow] = useState(Date.now());
  const [expandedReasonIds, setExpandedReasonIds] = useState<string[]>([]);

  const load = useCallback(async () => {
    const result = await api.getImpersonationDashboard();
    if (!abortController.current.signal.aborted) {
      setDashboard({ receivedRequests: result.receivedRequests || [] });
    }
  }, [api, abortController]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  const notifyValidationErrors = (errors: PropertyError[]) => {
    errors.forEach((propertyError) => {
      propertyError.errors.forEach((error) => enqueueSnackbar(error.message, { variant: 'error' }));
    });
  };

  const respond = async (request: IImpersonationRequest, accept: boolean) => {
    setRespondingId(request.id);
    try {
      let responded = false;
      await api.invoke(
        async (client) => {
          if (accept) {
            await client.acceptImpersonationRequest(request.id);
          } else {
            await client.rejectImpersonationRequest(request.id);
          }
          responded = true;
        },
        abortController.current,
        notifyValidationErrors,
        notifyUnhandledException,
      );
      if (!responded) {
        await load();
        return;
      }
      enqueueSnackbar(
        formatMessage({
          id: accept ? 'dashboard.impersonation.accepted' : 'dashboard.impersonation.rejected',
        }),
        { variant: accept ? 'success' : 'error' },
      );
      await load();
    } catch {
      notifyUnhandledException();
      await load().catch(() => undefined);
    } finally {
      setRespondingId(undefined);
    }
  };

  const deleteRequest = async (request: IImpersonationRequest) => {
    setRespondingId(request.id);
    try {
      let deleted = false;
      await api.invoke(
        async (client) => {
          await client.deleteImpersonationRequest(request.id);
          deleted = true;
        },
        abortController.current,
        notifyValidationErrors,
        notifyUnhandledException,
      );
      if (!deleted) {
        await load();
        return;
      }
      enqueueSnackbar(formatMessage({ id: 'dashboard.impersonation.dismissed' }), { variant: 'success' });
      await load();
    } catch {
      notifyUnhandledException();
      await load().catch(() => undefined);
    } finally {
      setRespondingId(undefined);
    }
  };

  const formatTimeLeft = (respondBeforeUTC: Date) => {
    const milliseconds = Math.max(moment.utc(respondBeforeUTC).valueOf() - now, 0);
    const duration = moment.duration(milliseconds);
    const minutes = Math.floor(duration.asMinutes());
    const seconds = duration.seconds();
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const received = dashboard.receivedRequests;

  if (received.length === 0) {
    return null;
  }

  return (
    <Grid item className={classes.widgetGridItem}>
      <Box className={classes.requestList}>
        {received.map((request) => {
          const isLongReason = (request.reason?.length ?? 0) > reasonPreviewLength;
          const isReasonExpanded = expandedReasonIds.includes(request.id);
          return (
          <Card key={request.id} className={classes.requestCard}>
            <CardHeader
              className={classes.cardHeader}
              avatar={<Avatar className={classes.avatar}><Security /></Avatar>}
              title={
                <Box className={classes.headingRow}>
                  <Typography variant="h4" className={classes.heading}>{formatMessage({ id: 'dashboard.impersonation.invite-title' })}</Typography>
                  <Chip
                    size="small"
                    label={formatMessage({
                      id: request.status === 'pending' && moment.utc(request.respondBeforeUTC).valueOf() <= now
                        ? 'admin.impersonations.filter.expired'
                        : 'dashboard.impersonation.pending',
                    })}
                    className={
                      moment.utc(request.respondBeforeUTC).valueOf() <= now
                        ? classes.expiredChip
                        : classes.pendingChip
                    }
                  />
                </Box>
              }
              action={request.status === 'pending' ? (
                <Chip
                  size="small"
                  icon={<Timer />}
                  label={formatTimeLeft(request.respondBeforeUTC)}
                  className={classes.timer}
                />
              ) : undefined}
            />
            <CardContent className={classes.requestContent}>
              <Typography variant="body2">{formatMessage({ id: 'dashboard.impersonation.requested-by' })}: {request.requestedByName}</Typography>
              {request.reason && (
                <Box className={classes.reasonRow}>
                  <Typography variant="body2" className={classes.reasonLabel}>
                    {formatMessage({ id: 'dashboard.impersonation.reason' })}:
                  </Typography>
                  <Box className={isReasonExpanded ? classes.reasonValueExpanded : classes.reasonValue}>
                    <Typography component="span" variant="body2" className={isReasonExpanded ? classes.reasonExpanded : classes.reasonPreview}>
                      {request.reason}
                    </Typography>
                    {isLongReason && (
                      <MuiLink
                        className={classes.showMoreLink}
                        component="button"
                        type="button"
                        onClick={() => setExpandedReasonIds((current) => (
                          isReasonExpanded
                            ? current.filter((id) => id !== request.id)
                            : [...current, request.id]
                        ))}
                      >
                        {formatMessage({ id: isReasonExpanded ? 'utils.show-less' : 'utils.show-more' })}
                      </MuiLink>
                    )}
                  </Box>
                </Box>
              )}
              <Typography variant="body2" color="textSecondary">
                {formatMessage({ id: 'dashboard.impersonation.respond-before' })}: {moment.utc(request.respondBeforeUTC).local().format('lll')}
              </Typography>
            </CardContent>
            <CardActions className={classes.actions}>
              {moment.utc(request.respondBeforeUTC).valueOf() <= now ? (
                <Button className={classes.deleteButton} variant="contained" startIcon={<DeleteOutline />} disabled={!!respondingId} onClick={() => deleteRequest(request)}>
                  {formatMessage({ id: 'form.delete' })}
                </Button>
              ) : (
                <>
                  <Button className={classes.acceptButton} variant="contained" startIcon={<CheckCircleOutline />} disabled={!!respondingId} onClick={() => respond(request, true)}>
                    {formatMessage({ id: 'dashboard.impersonation.accept' })}
                  </Button>
                  <Button className={classes.rejectButton} variant="contained" startIcon={<Block />} disabled={!!respondingId} onClick={() => respond(request, false)}>
                    {formatMessage({ id: 'dashboard.impersonation.reject' })}
                  </Button>
                </>
              )}
            </CardActions>
          </Card>
          );
        })}
      </Box>
    </Grid>
  );
};
